import { SCHEDULER_VARIANTS } from "@/app/smile/schedule/variants";
import { toBookingRouteHref, type BookingRoute } from "@/lib/utils/normalize-slot-hrefs";

/**
 * The ONE place a /smile page's booking route is decided.
 *
 * D1 (Chris, 2026-09-08, adopting Nathan Schwederske's 2026-09-01 ruling):
 * every authored Book CTA on a /smile surface goes to TAG's external scheduler
 * (/schedule-an-appointment/, office pin and authored params kept verbatim).
 * Until 2026-09-10 the emergency PAGES were the exception, routing to the
 * internal dispatcher /smile/schedule/?variant=emergency. Eric Zapfe (TAG,
 * Teams 2026-09-10) closed that exception: "Emergency scheduler link -
 * currently pointing to the scheduler not in use. Needs updated to BAU
 * schedule link ... Should be: /schedule-an-appointment/emergency/". So every
 * PAGE slug routes external now, and the emergency LPs carry TAG's emergency
 * reason path (EXTERNAL_REASON_PATHS, smile-book-href.ts) the way dentures or
 * implants carry theirs. The one surface still on the "emergency" route is the
 * standalone /smile/schedule/?variant=emergency page itself
 * (bookingRouteForScheduleRoute): it IS the dispatcher, and its own chrome
 * keeps booking where it stands. D2 (same ruling): no page embeds a scheduler
 * at launch.
 *
 * Two pre-existing registries feed the decision:
 *   - SMILE_BOOK_CTA_VARIANTS (below): page slug -> scheduler variant slug.
 *     The rows are the stamped bands' own authored hrefs (CMA census
 *     2026-08-31, chrome-canon perPage) plus smile/emergency-scheduler
 *     (TAG-185). They name which pages are scheduler LPs.
 *   - SCHEDULER_VARIANTS (app/smile/schedule/variants.ts): variant slug ->
 *     scheduler type. Read by bookingRouteForVariant for the /smile/schedule/
 *     route only: its emergency-fastpath family keeps that route's chrome
 *     internal; every other variant, and every PAGE slug (known or not),
 *     resolves external, so a new page fails toward TAG's scheduler.
 *
 * The seams that carry page context ask here, and pass the answer down as
 * `bookingRoute` rather than re-deciding it:
 *   - app/[...slug]/page.tsx (bookingRouteForSlug once per page) -> the shared
 *     header slot fetch (getSlotExperience -> normalizeSlotHrefs pass 1), the
 *     band enhancers (normalizeBandHrefs) and the <600px chrome
 *     (toSmileBookHref), plus applyBookCtaVariant on the slot JSON;
 *   - app/components/ExperienceWrapper/ExperienceWrapper.tsx, for the page's
 *     own embedded band (normalizeSlotHrefs pass 1);
 *   - app/@footer/FooterSlot.tsx, for an entry-authored footer slot
 *     (bookingRouteForSlug by page slug; bookingRouteForScheduleRoute on the
 *     no-slug /smile/schedule/ mount, external on every other no-slug mount);
 *   - app/smile/schedule/page.tsx (bookingRouteForScheduleRoute, by the
 *     ?variant= it serves);
 *   - app/components/studio/LocationSearch (getScheduleUrl, the office-card
 *     Schedule links, by the pathname it renders on).
 * Two seams render Book hrefs from props with NO page context and so cannot
 * ask: the SmileHeader studio atom and SmileButton. They render the authored
 * value when it is same-origin (smile-book-href.ts) and default to TAG's
 * scheduler with the page's reason path.
 *
 * History: until D1 this module re-applied each page's variant to the shared
 * header's Book CTA so the per-page embedded schedulers kept their funnel
 * (/smile/schedule/?variant=dentures on the dentures page, and so on). Under
 * D1 only the emergency rewrite remained; since 2026-09-10 no page rewrite
 * remains at all. The rows stay because they are the registry the reason
 * paths read, and embedding is to be tested later.
 */

/**
 * Plain page slug (composable prefix already stripped) to scheduler variant.
 * Source of truth: chrome-canon.json perPage, 2026-08-31 census.
 */
export const SMILE_BOOK_CTA_VARIANTS: Readonly<Record<string, string>> = {
  "smile/implants": "implants",
  "smile/dental-implants": "implants",
  "smile/tooth-replacement": "tooth-replacement",
  "smile/dentures": "dentures",
  "smile/dentures/repair-replace": "denture-repair",
  "smile/emergency": "emergency",
  // TAG-185: this page's own CTAs fell through to the general dispatcher
  // because the slug was missing from the map, kicking emergency patients out
  // of the emergency flow. Same emergency variant as its sibling row.
  "smile/emergency-scheduler": "emergency",
  "smile/clear-aligners": "clear-aligners",
  "smile/motto-clear-aligners": "clear-aligners",
  // D15 (Chris 2026-09-08): checkup books TAG's Other reason. The stamped
  // checkup band already books /schedule-an-appointment/other/; without this
  // row the registry's checkup variant was unreachable by slug, so a code-
  // rendered Book CTA on the checkup page (the Smile Header atom, D9) fell to
  // the bare scheduler. Route stays external: checkup-preseeded is not the
  // emergency fastpath.
  "smile/checkup": "checkup",
};

export function bookCtaVariantForSlug(slugPath: string): string | null {
  return SMILE_BOOK_CTA_VARIANTS[slugPath] ?? null;
}

/**
 * Route for a scheduler variant slug: emergency-fastpath registry rows are
 * "emergency"; every other row, and any slug the registry does not know, is
 * "external". The /smile/schedule/ route uses this directly with its own
 * ?variant= param, since its surface is whatever variant it is serving.
 */
export function bookingRouteForVariant(variantSlug: string | null | undefined): BookingRoute {
  const row = variantSlug ? SCHEDULER_VARIANTS.find((v) => v.slug === variantSlug) : undefined;
  return row?.schedulerType === "emergency-fastpath" ? "emergency" : "external";
}

/**
 * Route for a plain page slug (composable prefix already stripped): external,
 * for every page. Eric Zapfe's 2026-09-10 directive (module comment) sends the
 * emergency LPs to TAG's BAU scheduler with the emergency reason path, so the
 * registry decides a page's REASON (bookCtaVariantForSlug -> EXTERNAL_REASON_PATHS
 * in smile-book-href.ts), never its route. Kept as the named seam every
 * page-context caller asks (page.tsx, FooterSlot, LocationSearch,
 * applyBookCtaVariant) so the decision cannot be re-derived elsewhere.
 */
export function bookingRouteForSlug(_pageSlug: string): BookingRoute {
  return "external";
}

/**
 * Route for the standalone /smile/schedule/ route's own chrome, by the
 * ?variant= it is serving. Settled reading of the ruling (Chris 2026-09-08):
 * the route decides by VARIANT. Only ?variant=emergency (an emergency-fastpath
 * registry row) keeps its chrome in the emergency flow; the bare chooser (no
 * ?variant=) and every non-emergency variant route external, like every
 * non-emergency /smile page. D2 naming this route an emergency BOOKING surface
 * is about where emergency Book CTAs land, not about what its own chrome does
 * while no emergency variant is being served. Kept as a named seam so
 * app/smile/schedule/page.tsx (header) and app/@footer/FooterSlot.tsx (the
 * no-slug mount on this route) make the identical call and cannot disagree.
 */
export function bookingRouteForScheduleRoute(variantSlug: string | null | undefined): BookingRoute {
  return bookingRouteForVariant(variantSlug);
}

/**
 * Page seam for the SHARED header slot JSON (app/[...slug]/page.tsx): re-applies
 * the page's booking route to every string value, so a slot that reached the
 * page without that context still agrees with getSlotExperience's own pass.
 * Since Eric Zapfe's 2026-09-10 directive no page slug routes emergency, so
 * every page comes back byte-identical (the TAG-185 dispatcher rewrite lives
 * on only for the /smile/schedule/ route, which never passes through here).
 * Value match, never node ids: ids are entry-local and a CMA re-check refuted
 * their stability on 7 of 14 pages (see embedded-chrome.ts). Runs on the chrome
 * slot's JSON only, never on the page experience; JSON that does not parse
 * comes back byte-identical too.
 */
export function applyBookCtaVariant(experienceJSON: string, slugPath: string): string {
  const route = bookingRouteForSlug(slugPath);
  if (route !== "emergency") return experienceJSON;
  let parsed: unknown;
  try {
    parsed = JSON.parse(experienceJSON);
  } catch {
    return experienceJSON;
  }
  const walk = (node: unknown): unknown => {
    if (typeof node === "string") return toBookingRouteHref(node, route);
    if (Array.isArray(node)) return node.map(walk);
    if (node !== null && typeof node === "object") {
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(node)) out[key] = walk(value);
      return out;
    }
    return node;
  };
  return JSON.stringify(walk(parsed));
}
